import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiAward,
  FiUsers,
  FiTrendingUp,
  FiCheckCircle,
  FiDollarSign,
  FiX,
  FiAlertCircle,
  FiActivity,
  FiClock,
  FiLink,
  FiEye,
  FiInfo,
  FiSearch,
  FiCalendar,
  FiRefreshCw,
  FiXCircle,
  FiRotateCcw,
} from 'react-icons/fi';
import { User, MasterTrader, Trade } from '../../types';
import { useApi } from '../../hooks/useApi';
import { useQueryClient } from '@tanstack/react-query';
import { Loader, LoadingButton, SkeletonCard } from '../Common/Loader';
import { ErrorDisplay } from '../Common/ErrorDisplay';
import CopySettingsModal from './CopySettingsModal';
import { showToast } from '../../utils/toast';
import { getProfileImageUrl, DEFAULT_AVATAR_URL } from '../../utils/imageUtils';

interface MastersListProps {
  user: User;
  refresh: () => void;
  selectedBrokerId?: string | null;
}

interface MasterWithDetails extends MasterTrader {
  hasBrokerageConnected?: boolean;
  canCopy?: boolean;
  isFollowing?: boolean;
  relationshipId?: string;
  /** 'active' | 'paused' | 'pending' - pending = will activate when master has no open positions */
  relationshipStatus?: 'active' | 'paused' | 'pending';
  recentTrades?: Trade[];
  createdAt?: string;
  tradingType?: 'swing' | 'day';
  tradingTypeDescription?: string;
}

function brokerAccountToPlatformLabel(acc: { connectionType?: string; brokerageName?: string }): string | null {
  const type = (acc.connectionType || '').toLowerCase();
  const name = (acc.brokerageName || '').toLowerCase();
  if (type === 'oanda') return 'OANDA Master';
  if (type === 'genesis' || name.includes('genesis')) return 'Genesis Master';
  if (type === 'alpaca' || name.includes('alpaca')) return 'Alpaca Master';
  if (type === 'snaptrade') {
    if (name.includes('webull')) return 'Webull Master';
    const bn = acc.brokerageName?.trim();
    if (bn) return `${bn} Master`;
    return 'SnapTrade Master';
  }
  return null;
}

function getMasterPlatformLabels(accounts?: MasterTrader['brokerAccounts']): string[] {
  if (!accounts?.length) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const acc of accounts) {
    const label = brokerAccountToPlatformLabel(acc);
    if (label && !seen.has(label)) {
      seen.add(label);
      out.push(label);
    }
  }
  return out;
}

function platformMasterBadgeClass(label: string): string {
  if (label.startsWith('OANDA')) {
    return 'border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300';
  }
  if (label.startsWith('Webull')) {
    return 'border-sky-500/40 bg-sky-500/10 text-sky-800 dark:text-sky-300';
  }
  if (label.startsWith('Genesis')) {
    return 'border-emerald-500/40 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300';
  }
  if (label.startsWith('Alpaca')) {
    return 'border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300';
  }
  return 'border-border bg-muted/40 text-muted-foreground';
}

function getBrokerDisplayName(acc: { connectionType?: string; brokerageName?: string }): string {
  const platform = brokerAccountToPlatformLabel(acc);
  if (platform) return platform.replace(/ Master$/, '');
  const name = acc.brokerageName?.trim();
  if (name) return name;
  const type = (acc.connectionType || '').toLowerCase();
  if (type === 'oanda') return 'OANDA';
  if (type === 'genesis') return 'Genesis';
  if (type === 'alpaca') return 'Alpaca';
  if (type === 'snaptrade') return 'SnapTrade';
  return 'selected broker';
}

function isMasterCompatibleWithFollowerBroker(
  master: MasterWithDetails,
  followerBroker: { connectionType?: string; brokerageName?: string } | null | undefined
): boolean {
  if (!followerBroker) return true;
  const followerPlatform = brokerAccountToPlatformLabel(followerBroker);
  if (!followerPlatform) return true;
  return getMasterPlatformLabels(master.brokerAccounts).includes(followerPlatform);
}

const MastersList: React.FC<MastersListProps> = ({ user, refresh, selectedBrokerId }) => {
  const navigate = useNavigate();

  const { data: mastersData, isLoading: loading, error } = useApi.useMasters(selectedBrokerId, {
    enabled: user.role === 'follower',
  });

  const { data: connectionsData } = useApi.useBrokerageConnections({
    enabled: user.role === 'follower',
  });

  const { data: subscriptionData } = useApi.useSubscriptionStatus({
    enabled: user.role === 'follower',
  });

  const createCheckoutMutation = useApi.useCreateCheckoutSession({
    onError: (error) => {
      showToast('error', error.message || 'Failed to start checkout.');
    },
  });

  const queryClient = useQueryClient();

  const selectedFollowerBroker = useMemo(() => {
    const activeConnections = connectionsData?.connections?.filter((c) => c.status === 'active') ?? [];
    if (!activeConnections.length) return null;
    if (selectedBrokerId) {
      return activeConnections.find((c) => c._id === selectedBrokerId) || null;
    }
    return activeConnections.find((c) => c.isPrimary) || activeConnections[0];
  }, [selectedBrokerId, connectionsData?.connections]);

  const hasActiveBrokerConnection = useMemo(() => {
    if (user.hasBrokerageConnected) return true;
    return (connectionsData?.connections?.some((c) => c.status === 'active') ?? false);
  }, [user.hasBrokerageConnected, connectionsData?.connections]);

  const sidebarPlatformLabel = useMemo(() => {
    if (!selectedFollowerBroker) return null;
    return brokerAccountToPlatformLabel(selectedFollowerBroker);
  }, [selectedFollowerBroker]);

  const [selectedMaster, setSelectedMaster] = useState<MasterWithDetails | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [closePositions, setClosePositions] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');
  const userPickedAllMastersRef = useRef(false);

  const allMastersRaw = (mastersData?.masters || []) as MasterWithDetails[];
  // Only hide Following when we know there is no broker — do not strip while connections are still loading
  // (that was forcing Follow UI until full page reload after a successful follow).
  const allMasters = !hasActiveBrokerConnection
    ? allMastersRaw.map((m) =>
        m.isFollowing
          ? { ...m, isFollowing: false, relationshipId: undefined, relationshipStatus: undefined }
          : m
      )
    : allMastersRaw;
  const platformFilterOptions = useMemo(
    () => mastersData?.platformFilters ?? [],
    [mastersData?.platformFilters]
  );

  useEffect(() => {
    userPickedAllMastersRef.current = false;
  }, [selectedBrokerId]);

  useEffect(() => {
    if (!hasActiveBrokerConnection) {
      userPickedAllMastersRef.current = false;
      setPlatformFilter('all');
      return;
    }
    if (userPickedAllMastersRef.current) return;
    if (!sidebarPlatformLabel) return;
    const availableValues = platformFilterOptions.map((option) => option.value);
    if (availableValues.includes(sidebarPlatformLabel)) {
      setPlatformFilter(sidebarPlatformLabel);
    }
  }, [hasActiveBrokerConnection, selectedBrokerId, sidebarPlatformLabel, platformFilterOptions]);

  useEffect(() => {
    if (platformFilter === 'all') return;
    const availableValues = platformFilterOptions.map((option) => option.value);
    if (!availableValues.includes(platformFilter)) {
      if (
        hasActiveBrokerConnection &&
        sidebarPlatformLabel &&
        availableValues.includes(sidebarPlatformLabel)
      ) {
        setPlatformFilter(sidebarPlatformLabel);
      } else {
        setPlatformFilter('all');
      }
    }
  }, [platformFilterOptions, platformFilter, hasActiveBrokerConnection, sidebarPlatformLabel]);

  const applyUserSearch = () => {
    setAppliedSearch(searchQuery.trim());
  };

  const masters = allMasters.filter(master => {
    if (platformFilter !== 'all') {
      const labels = getMasterPlatformLabels(master.brokerAccounts);
      if (!labels.includes(platformFilter)) return false;
    }
    if (!appliedSearch) return true;
    const query = appliedSearch.toLowerCase();
    const platformMatch = getMasterPlatformLabels(master.brokerAccounts).some((l) =>
      l.toLowerCase().includes(query)
    );
    return (
      master.name?.toLowerCase().includes(query) ||
      master.email?.toLowerCase().includes(query) ||
      (master.tradingType && master.tradingType.toLowerCase().includes(query)) ||
      (master.tradingType === 'swing' && 'swing trading'.includes(query)) ||
      (master.tradingType === 'day' && ('day trading'.includes(query) || 'hedge trading'.includes(query))) ||
      platformMatch
    );
  });

  // TEMP: master-follow cap disabled for every plan
  // const followedMasterCount = useMemo(
  //   () => allMasters.filter((m) => m.isFollowing).length,
  //   [allMasters]
  // );
  // const atMasterFollowLimit = Number.isFinite(maxMastersAllowed) && followedMasterCount >= maxMastersAllowed;

  const hasActiveFilters = appliedSearch.length > 0 || platformFilter !== 'all';

  const handleMasterClick = (master: MasterWithDetails, event: React.MouseEvent) => {
    // If clicking the "View Details" button, navigate to details page
    if ((event.target as HTMLElement).closest('.view-details-btn')) {
      event.stopPropagation();
      navigate(`/master-details/${master._id}`);
      return;
    }

    // Otherwise show quick modal
    setSelectedMaster(master);
    setShowDetailsModal(true);
  };

  const tryStartCopying = (master: MasterWithDetails) => {
    if (!master.hasBrokerageConnected || !user.hasBrokerageConnected) return;

    if (selectedFollowerBroker && !isMasterCompatibleWithFollowerBroker(master, selectedFollowerBroker)) {
      const brokerName = getBrokerDisplayName(selectedFollowerBroker);
      showToast('error', `Please follow a master related to ${brokerName}`);
      return;
    }

    setSelectedMaster(master);
    setShowSettingsModal(true);
  };

  const handleCopyNowClick = () => {
    if (!selectedMaster) return;
    if (selectedFollowerBroker && !isMasterCompatibleWithFollowerBroker(selectedMaster, selectedFollowerBroker)) {
      const brokerName = getBrokerDisplayName(selectedFollowerBroker);
      showToast('error', `Please follow a master related to ${brokerName}`);
      return;
    }
    setShowDetailsModal(false);
    setShowSettingsModal(true);
  };


  const cancelMutation = useApi.useCancelCopyTrading({
    onSuccess: (data: { success: boolean; message?: string; closedPositions?: number }) => {
      const message = data.message || 'Copy trading cancelled successfully';
      showToast('success', message);
      setShowCancelModal(false);
      setSelectedMaster(null);
      refresh();
    },
    onError: (err: any) => {
      showToast('error', err.response?.data?.error || 'Failed to cancel copy trading');
    },
  });

  const handleCopySuccess = () => {
    setShowSettingsModal(false);
    setShowDetailsModal(false);
    setSelectedMaster(null);
    refresh(); // dashboard aggregate; masters list already refetched in enable-copy mutations
  };

  const handleUnfollowClick = (master: MasterWithDetails) => {
    setSelectedMaster(master);
    setShowCancelModal(true);
  };

  const handleConfirmCancel = () => {
    if (!selectedMaster || !selectedMaster.relationshipId) return;
    cancelMutation.mutate({
      relationshipId: selectedMaster.relationshipId,
      closePositions,
    });
  };

  // Show error if user is not a follower
  if (user.role !== 'follower') {
    return (
      <div className="text-center py-12">
        <ErrorDisplay error="This page is only available for follower accounts." />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-8 animate-fade-in">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-14 h-14 shrink-0 rounded-xl bg-muted/20 animate-pulse"></div>
            <div className="space-y-2 min-w-0">
              <div className="h-8 w-48 max-w-full bg-muted/20 rounded animate-pulse"></div>
              <div className="h-4 w-64 max-w-full bg-muted/20 rounded animate-pulse"></div>
            </div>
          </div>
          <div className="h-8 w-24 bg-muted/20 rounded animate-pulse"></div>
        </div>

        {/* Master Cards Skeleton */}
        <div className="space-y-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card-premium">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 lg:gap-6">
                <div className="flex items-center gap-3 sm:gap-5 flex-1 min-w-0">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 shrink-0 rounded-2xl bg-muted/20 animate-pulse"></div>
                  <div className="space-y-2 min-w-0">
                    <div className="h-6 w-40 max-w-full bg-muted/20 rounded animate-pulse"></div>
                    <div className="h-4 w-32 max-w-full bg-muted/20 rounded animate-pulse"></div>
                    <div className="h-6 w-28 max-w-full bg-muted/20 rounded animate-pulse"></div>
                  </div>
                </div>
                <div className="flex gap-2 sm:gap-4">
                  {[1, 2, 3].map((j) => (
                    <div key={j} className="flex-1 lg:flex-none text-center px-2 sm:px-4 py-3 bg-surface-elevated rounded-xl border border-border">
                      <div className="h-5 w-5 bg-muted/20 rounded mb-2 mx-auto animate-pulse"></div>
                      <div className="h-6 w-16 bg-muted/20 rounded mb-1 mx-auto animate-pulse"></div>
                      <div className="h-3 w-12 bg-muted/20 rounded mx-auto animate-pulse"></div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-row lg:flex-col gap-2">
                  <div className="h-10 flex-1 lg:flex-none lg:w-28 bg-muted/20 rounded-lg animate-pulse"></div>
                  <div className="h-10 flex-1 lg:flex-none lg:w-28 bg-muted/20 rounded-lg animate-pulse"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return <ErrorDisplay error={error} />;
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-br from-accent-500 to-accent-600 lg:p-3 p-2 rounded-xl shadow-lg shadow-accent-500/30">
            <FiAward className="lg:h-8 h-6 lg:w-8 w-6 text-white" />
          </div>
          <div>
            <h2 className="lg:text-4xl text-xl title-page">
              Master Traders
            </h2>
            <p className="text-muted lg:mt-2 mt-1 lg:text-lg text-[14px]">Discover and follow verified expert traders</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {user.role === 'follower' && (
            <button
              type="button"
              onClick={() => navigate('/become-master-trader')}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 border-primary/30 bg-primary/10 text-primary text-sm font-semibold hover:bg-primary/15 transition-colors"
            >
              <FiAward className="h-4 w-4" />
              Become Master Trader
            </button>
          )}
          <span className="text-sm font-medium text-muted px-4 py-2 bg-surface-elevated rounded-sm border border-border">
            {masters.length} {masters.length === 1 ? 'Master' : 'Masters'}
          </span>
        </div>
      </div>


      {/* Search Filter */}
      <div className="card-premium">
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="relative flex-1 min-w-0">
            <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted pointer-events-none" />
            <input
              type="text"
              placeholder="Search masters by name, email, or trading type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyUserSearch();
                }
              }}
              className={`w-full pl-12 py-3 bg-surface border-2 border-border rounded-lg text-foreground placeholder-muted focus:outline-none focus:border-primary transition-colors ${searchQuery || appliedSearch ? 'pr-11' : 'pr-4'}`}
            />
            {(searchQuery || appliedSearch) && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setAppliedSearch('');
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-foreground p-1 rounded-md transition-colors"
                aria-label="Clear search"
              >
                <FiX className="h-4 w-4" />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={applyUserSearch}
            className="btn-primary shrink-0 px-5 py-3 flex items-center justify-center gap-2 whitespace-nowrap font-semibold"
          >
            <FiSearch className="h-4 w-4" />
            Search
          </button>
        </div>

        {platformFilterOptions.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <label htmlFor="master-platform-filter" className="text-sm font-medium text-muted shrink-0">
              Filter by Brokerage platform
            </label>
            <select
              id="master-platform-filter"
              value={platformFilter}
              onChange={(e) => {
                userPickedAllMastersRef.current = e.target.value === 'all';
                setPlatformFilter(e.target.value);
              }}
              className="w-full sm:w-auto sm:min-w-[220px] px-4 py-2.5 bg-surface border-2 border-border rounded-lg text-foreground font-medium focus:outline-none focus:border-primary transition-colors"
            >
              <option value="all">All Masters</option>
              {platformFilterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Balance Requirement Note for All Followers */}
      {/* <div className="card-premium border-primary/30 bg-primary/5">
        <div className="flex items-start gap-4">
          <div className="bg-primary/20 p-3 rounded-lg flex-shrink-0">
            <FiInfo className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1">
            <p className="text-foreground">
              💡 <strong>Balance Requirements:</strong> To start copying a master trader, your account must meet the minimum balance requirement based on their trading type:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-sm text-muted">
              <li><strong>Swing Trading:</strong> Minimum $20,000 balance required</li>
              <li><strong>Day Trading / Hedge Trading:</strong> Minimum $1,000-$2,000 balance required</li>
            </ul>
            <p className="text-sm text-muted mt-2">This ensures you have sufficient capital to properly copy trades based on the trading strategy. Masters are exempt from this requirement.</p>
          </div>
        </div>
      </div> */}

      {masters.length === 0 ? (
        <div className="card-premium text-center py-16">
          <div className="bg-primary/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
            <FiAward className="h-10 w-10 text-primary" />
          </div>
          <h3 className="lg:text-xl text-md font-bold text-foreground mb-2">
            {allMasters.length > 0 && hasActiveFilters
              ? 'No matching masters'
              : 'No Master Traders Yet'}
          </h3>
          <p className="text-muted lg:text-lg text-[14px]">
            {allMasters.length > 0 && hasActiveFilters
              ? 'Try different keywords, change the platform filter, or clear your filters.'
              : 'Check back soon for expert traders to follow'}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-6">
            {masters.map((master) => (
              <div
                key={master._id}
                className="card-premium group hover:-translate-y-1 hover:shadow-2xl transition-all duration-300 relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary-500/10 to-transparent  blur-2xl"></div>
                {master.isFollowing && (
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary-600 to-accent-500"></div>
                )}

                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 lg:gap-6 relative z-10">
                  <div className="flex items-center gap-3 sm:gap-5 flex-1 min-w-0">
                    <div className="relative shrink-0">
                      {master.profileImage ? (
                        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-sm overflow-hidden border-2 border-border shadow-lg group-hover:scale-105 transition-all duration-300">
                          <img
                            src={getProfileImageUrl(master.profileImage)}
                            alt={master.name}
                            crossOrigin="anonymous"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = DEFAULT_AVATAR_URL;
                            }}
                          />
                        </div>
                      ) : (
                        <div className="bg-gradient-to-br from-accent-500 to-accent-600 p-3 sm:p-4 rounded-2xl shadow-lg shadow-accent-500/30 group-hover:shadow-xl group-hover:shadow-accent-500/40 group-hover:scale-110 transition-all duration-300">
                          <FiAward className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
                        </div>
                      )}
                      {master.isFollowing && (
                        <div className="absolute -top-2 -right-2 bg-gradient-to-br from-primary-600 to-primary-500 rounded-full p-2 shadow-lg border-2 border-surface">
                          <FiCheckCircle className="h-4 w-4 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
                        <h3 className="font-bold text-xl sm:text-2xl text-foreground break-anywhere min-w-0">{master.name}</h3>
                        
                        {master.tradingType && (
                          <span className={`px-2.5 py-1 text-xs font-semibold flex items-center gap-1 ${master.tradingType === 'swing'
                            ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            }`}>
                            {master.tradingType === 'swing' ? <FiCalendar className="h-3 w-3" /> : <FiClock className="h-3 w-3" />}
                            {master.tradingType === 'swing' ? 'Swing Trader' : 'Day Trader'}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {master.hasBrokerageConnected ? (
                          <span className="badge-success flex items-center gap-1.5 px-3 py-1.5 text-xs">
                            <FiLink className="h-3.5 w-3.5" />
                            Live Trading Active
                          </span>
                        ) : (
                          <span className="badge-danger flex items-center gap-1.5 px-3 py-1.5 text-xs">
                            <FiAlertCircle className="h-3.5 w-3.5" />
                            Not Connected
                          </span>
                        )}
                        {master.isFollowing && (
                          <span className="badge-primary flex items-center gap-1.5 px-3 py-1.5 text-xs">
                            <FiCheckCircle className="h-3.5 w-3.5" />
                            Following
                          </span>
                        )}
                        {getMasterPlatformLabels(master.brokerAccounts).map((label) => (
                          <span
                            key={label}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border ${platformMasterBadgeClass(label)}`}
                          >
                            <FiLink className="h-3.5 w-3.5 shrink-0 opacity-80" />
                            {label}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-3 lg:flex lg:items-center gap-2 sm:gap-4 lg:gap-6">
                    <div className="min-w-0 text-center px-2 sm:px-4 py-3 bg-surface-elevated rounded-sm border border-border">
                      <div className="flex items-center justify-center lg:justify-start gap-2 text-primary mb-1">
                        <FiDollarSign className="h-5 w-5" />
                      </div>
                      <p className="font-bold text-base sm:text-xl text-foreground tabular-nums break-anywhere">
                        ${master.accountBalance?.toLocaleString() || '0'}
                      </p>
                      <span className="text-[10px] sm:text-xs text-muted uppercase tracking-wide">Balance</span>
                    </div>
                    <div className="min-w-0 text-center px-2 sm:px-4 py-3 bg-surface-elevated rounded-sm border border-border">
                      <div className="flex items-center justify-center lg:justify-start gap-2 text-accent mb-1">
                        <FiUsers className="h-5 w-5" />
                      </div>
                      <p className="font-bold text-base sm:text-xl text-foreground tabular-nums">{master.followerCount}</p>
                      <span className="text-[10px] sm:text-xs text-muted uppercase tracking-wide">Followers</span>
                    </div>
                    <div className="min-w-0 text-center px-2 sm:px-4 py-3 bg-surface-elevated rounded-sm border border-border">
                      <div className="flex items-center justify-center lg:justify-start gap-2 text-info mb-1">
                        <FiTrendingUp className="h-5 w-5" />
                      </div>
                      <p className="font-bold text-base sm:text-xl text-foreground tabular-nums">{master.totalTrades || 0}</p>
                      <span className="text-[10px] sm:text-xs text-muted uppercase tracking-wide">Trades</span>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex flex-col sm:flex-row lg:flex-col gap-2 w-full lg:w-auto">
                    <button
                      className="view-details-btn btn-ghost px-5 py-2.5 flex items-center justify-center lg:justify-start gap-2 whitespace-nowrap w-full sm:w-auto sm:flex-1 lg:flex-none"
                      onClick={() => navigate(`/master-details/${master._id}`)}
                    >
                      <FiEye className="h-4 w-4" />
                      View Profile
                    </button>
                    {master.isFollowing ? (
                      <div className="flex flex-col gap-2 w-full sm:w-auto sm:flex-1 lg:flex-none">
                        {master.relationshipStatus === 'pending' && (
                          <p className="text-xs text-amber-600 dark:text-amber-400 font-medium flex items-center gap-1">
                            <FiClock className="h-3.5 w-3.5" />
                            Pending — you&apos;ll start copying when this master has no open positions
                          </p>
                        )}
                        <button
                          className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-sm flex items-center justify-center gap-2 whitespace-nowrap transition-colors disabled:opacity-50"
                          onClick={() => handleUnfollowClick(master)}
                          disabled={cancelMutation.isPending && selectedMaster?._id === master._id}
                        >
                          {cancelMutation.isPending && selectedMaster?._id === master._id ? (
                            <>
                              <FiRefreshCw className="h-4 w-4 animate-spin" />
                              Unfollowing...
                            </>
                          ) : (
                            <>
                              <FiX className="h-4 w-4" />
                              Unfollow
                            </>
                          )}
                        </button>
                      </div>
                    ) : (
                      <button
                        className="btn-primary px-5 py-2.5 flex flex-col items-center justify-center gap-0.5 whitespace-nowrap text-sm disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto sm:flex-1 lg:flex-none lg:min-w-[8.5rem]"
                        onClick={() => tryStartCopying(master)}
                        disabled={!master.hasBrokerageConnected || !user.hasBrokerageConnected || createCheckoutMutation.isPending}
                        title={!master.hasBrokerageConnected ? "Master hasn't connected brokerage" : !user.hasBrokerageConnected ? "Connect your brokerage first" : "Start copying trades"}
                      >
                        <span className="flex items-center gap-2">
                          <FiCheckCircle className="h-4 w-4" />
                          Start Copying
                        </span>
                        {/* Pay label removed from master list */}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Master Details Modal */}
          {showDetailsModal && selectedMaster && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4">
              <div className="bg-surface-elevated rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-border">
                {/* Header */}
                <div className="sticky top-0 bg-surface-elevated border-b border-border px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                    <div className="p-2 sm:p-3 bg-primary/10 rounded-full shrink-0">
                      <FiAward className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-xl sm:text-2xl font-bold text-foreground break-anywhere">{selectedMaster.name}</h2>
                      <p className="text-muted break-anywhere">{selectedMaster.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      setSelectedMaster(null);
                    }}
                    className="text-muted hover:text-foreground transition-colors shrink-0"
                  >
                    <FiX className="h-6 w-6" />
                  </button>
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6 space-y-6">
                  {/* Brokerage Status Alert */}
                  {!selectedMaster.hasBrokerageConnected && (
                    <div className="bg-danger/10 border-2 border-danger/30 rounded-lg p-4 flex items-start gap-3">
                      <FiAlertCircle className="h-6 w-6 text-danger flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-bold text-foreground mb-1">Cannot Enable Copy Trading</h4>
                        <p className="text-sm text-muted">
                          This master trader hasn't connected their brokerage account yet. Copy trading requires an active
                          brokerage connection to sync trades in real-time.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                    <div className="bg-surface rounded-lg p-3 sm:p-4 border border-border min-w-0">
                      <div className="flex items-center gap-2 text-muted mb-2">
                        <FiDollarSign className="h-5 w-5" />
                        <span className="text-sm font-medium">Balance</span>
                      </div>
                      <p className="text-xl sm:text-2xl font-bold text-foreground break-anywhere">
                        ${selectedMaster.accountBalance?.toLocaleString() || '0'}
                      </p>
                    </div>
                    <div className="bg-surface rounded-lg p-3 sm:p-4 border border-border min-w-0">
                      <div className="flex items-center gap-2 text-muted mb-2">
                        <FiUsers className="h-5 w-5" />
                        <span className="text-sm font-medium">Followers</span>
                      </div>
                      <p className="text-2xl font-bold text-foreground">{selectedMaster.followerCount}</p>
                    </div>
                    <div className="bg-surface rounded-lg p-3 sm:p-4 border border-border min-w-0">
                      <div className="flex items-center gap-2 text-muted mb-2">
                        <FiTrendingUp className="h-5 w-5" />
                        <span className="text-sm font-medium">Total Trades</span>
                      </div>
                      <p className="text-2xl font-bold text-foreground">{selectedMaster.totalTrades || 0}</p>
                    </div>
                    <div className="bg-surface rounded-lg p-3 sm:p-4 border border-border min-w-0">
                      <div className="flex items-center gap-2 text-muted mb-2">
                        <FiCheckCircle className="h-5 w-5" />
                        <span className="text-sm font-medium">Success Rate</span>
                      </div>
                      <p className="text-2xl font-bold text-success">{selectedMaster.successRate}%</p>
                    </div>
                  </div>

                  {/* Recent Trades */}
                  {selectedMaster.recentTrades && selectedMaster.recentTrades.length > 0 && (
                    <div>
                      <h3 className="text-lg font-bold text-foreground mb-3 flex items-center gap-2">
                        <FiActivity className="h-5 w-5" />
                        Recent Trades
                      </h3>
                      <div className="space-y-2">
                        {selectedMaster.recentTrades.map((trade, idx) => (
                          <div key={idx} className={`p-3 rounded-lg border-2 ${trade.side === 'buy' ? 'border-success/30 bg-success/5' : 'border-danger/30 bg-danger/5'
                            }`}>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
                                <span className="font-bold text-foreground dark:text-black">{trade.symbol}</span>
                                <span className={`px-2 py-1 rounded text-xs font-semibold ${trade.side === 'buy' ? 'bg-success/20 text-success' : 'bg-danger/20 text-danger'
                                  }`}>
                                  {trade.side?.toUpperCase()}
                                </span>
                                <span className="text-muted">{trade.qty} shares</span>
                              </div>
                              <span className="text-sm text-muted flex items-center gap-1">
                                <FiClock className="h-3 w-3" />
                                {trade.createdAt && new Date(trade.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Pending follow notice */}
                  {selectedMaster.isFollowing && selectedMaster.relationshipStatus === 'pending' && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex items-start gap-3">
                      <FiClock className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-foreground mb-1">Copying pending</h4>
                        <p className="text-sm text-muted">
                          You will start copying when this master has no open positions (Oanda or US stocks). We check every 2 seconds and will activate automatically when their positions are closed.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Copy Trading Button */}
                  {selectedMaster.hasBrokerageConnected && (
                    <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 pt-4 border-t border-border">
                      <button
                        onClick={() => {
                          setShowDetailsModal(false);
                          setSelectedMaster(null);
                        }}
                        className="flex-1 px-6 py-3 border-2 border-border text-foreground font-semibold rounded-lg hover:bg-surface"
                      >
                        Close
                      </button>
                      {selectedMaster.isFollowing ? (
                        <button
                          onClick={() => {
                            setShowDetailsModal(false);
                            handleUnfollowClick(selectedMaster);
                          }}
                          className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2"
                          disabled={cancelMutation.isPending && selectedMaster?._id === selectedMaster._id}
                        >
                          {cancelMutation.isPending && selectedMaster?._id === selectedMaster._id ? (
                            <>
                              <FiRefreshCw className="h-4 w-4 animate-spin" />
                              Unfollowing...
                            </>
                          ) : (
                            <>
                              <FiX className="h-4 w-4" />
                              Unfollow
                            </>
                          )}
                        </button>
                      ) : (
                        <button
                          onClick={handleCopyNowClick}
                          className="flex-1 btn-primary flex flex-col items-center justify-center gap-0.5 text-lg py-3 disabled:opacity-50"
                          disabled={!user.hasBrokerageConnected || !selectedMaster?.hasBrokerageConnected || createCheckoutMutation.isPending}
                        >
                          <span className="flex items-center gap-2">
                            <FiCheckCircle className="h-5 w-5" />
                            Copy This Trader
                          </span>
                          {/* Pay label removed from master list */}
                        </button>
                      )}
                    </div>
                  )}

                  {!user.hasBrokerageConnected && (
                    <div className="bg-warning/10 border border-warning/30 rounded-lg p-4 flex items-start gap-3">
                      <FiAlertCircle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-foreground">
                        <strong>Action Required:</strong> Connect your brokerage account first to enable copy trading.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Copy Settings Modal */}
          {selectedMaster && (
            <CopySettingsModal
              master={selectedMaster}
              isOpen={showSettingsModal}
              defaultFollowerBrokerAccountId={selectedBrokerId}
              onClose={() => {
                setShowSettingsModal(false);
                setSelectedMaster(null);
              }}
              onSuccess={handleCopySuccess}
            />
          )}

          {/* Cancel/Unfollow Modal */}
          {showCancelModal && selectedMaster && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4">
              <div className="bg-surface-elevated rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto p-4 sm:p-6 border border-border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
                    <FiAlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground">Unfollow Master Trader</h3>
                </div>

                <p className="text-muted mb-4">
                  Are you sure you want to stop copying <strong className="text-foreground">{selectedMaster.name}</strong>?
                </p>

                <label className="flex items-start gap-3 p-4 bg-warning/10 border-2 border-warning/30 rounded-lg mb-6 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={closePositions}
                    onChange={(e) => setClosePositions(e.target.checked)}
                    className="w-5 h-5 text-primary-600 mt-0.5 accent-primary"
                  />
                  <div>
                    <p className="font-semibold text-foreground">Close all open positions</p>
                    <p className="text-sm text-muted mt-1">
                      This will automatically close all positions that were copied from this master trader.
                    </p>
                  </div>
                </label>

                <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
                  <button
                    onClick={() => {
                      setShowCancelModal(false);
                      setSelectedMaster(null);
                    }}
                    className="flex-1 px-4 py-2 border-2 border-border text-foreground font-semibold rounded-lg hover:bg-surface transition-colors"
                    disabled={cancelMutation.isPending}
                  >
                    Keep Following
                  </button>
                  <button
                    onClick={handleConfirmCancel}
                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors"
                    disabled={cancelMutation.isPending}
                  >
                    {cancelMutation.isPending ? 'Unfollowing...' : 'Unfollow'}
                  </button>
                </div>
              </div>
            </div>
          )}

        </>
      )}
    </div>
  );
};

export default MastersList;
